import {action, _, moment, exportAndDownloadService, observable} from '@api'
import {CatalogStore, IExportImagesConfig, IExportListValue, IExportVLPListValue, onSiteError} from '@stores'
import {downloadExcel} from '@utils'
import {computed} from 'mobx'
import {IExportOption, IExportHelperParams} from '@stores/config'

interface IExportObject {
  type: string,
  modalTitle: string
}

export interface IExportableField {
  field: string,
  label: string,
  type?: string,
  precision?: number
}

export interface IExportInput {
  id: string,
  index: string,
  name: string,
  fields: Array<IExportableField>,
  query: any,
  helperParams?: IExportHelperParams,
  nestedWorkspaces?: Array<IExportInput>,
  nameFields?: Array<string>
}

export interface IExportConfig {
  excel: IExportObject,
  images: IExportObject
}

export type ExportSelectedImageType = 'Thumbnail' | 'SmallImage' | 'Viewable'

export class ExportStore {
  constructor(public catalog: CatalogStore) {
    this.exportOptionsView = _.clone(this.defaultExportOptions)
  }
  @observable exportProgress = false
  @observable modalOpen = false
  @observable modalTitle

  @observable type
  @observable exportType: string
  @observable selectedImageSize: ExportSelectedImageType = 'Thumbnail'
  readonly EXPORT_IMAGE = 'images'

  @observable exportListValue: IExportListValue = {
    products: true,
    materials: true,
    colors: true,
    hardware: true,
    styles: true,
    notes: true,
    activity: true,
    images: true,
    tags: true
  }

  @observable exportVLPListValue: IExportVLPListValue = {
    merchOptions: true,
    merchProducts: true,
    tags: true
  }

  @computed get isImageExport() {
    return this.exportType === this.EXPORT_IMAGE
  }

  @computed get exportURL() {
    const {catalog: {stores: {config: {config: {graphqlEndpoint}}}}} = this,
      exportBaseUrl: string = `${graphqlEndpoint}excel/export/`
    return this.isImageExport ? `${exportBaseUrl}image` : `${exportBaseUrl}excel`
  }

  @computed get availableExports() {
    const {catalog: {exportConfig}} = this
    return Object.values(exportConfig ?? {}).filter(ex => ex?.enabled) || []
  }

  @computed get exportOptions(): Array<IExportOption> {
    if (!this?.catalog?.workspaceConfig?.catalog) return []
    const {catalog: {exportConfig}} = this
    return this.exportType ? exportConfig[this.exportType]?.modal?.exportOptions.filter(f => f?.value) : []
  }

  @computed get excelImageExportOptions(): Array<IExportOption> {
    if (!this?.catalog?.workspaceConfig?.catalog) return []
    const {catalog: {exportConfig}} = this
    return exportConfig[this.EXPORT_IMAGE]?.modal?.exportOptions?.filter(f => f?.value && f?.value !== 'Viewable') ?? []
  }

  @computed get imageExportOptions(): Array<IExportOption> {
    if (!this?.catalog?.workspaceConfig?.catalog) return []
    const {catalog: {exportConfig}} = this
    return exportConfig[this.EXPORT_IMAGE]?.modal?.exportOptions ?? []
  }

  @computed get selectedImageOption(): IExportOption {
    return this.imageExportOptions.find((o) => o.value === this.selectedImageSize)
  }

  @observable.ref exportOptionsView : { [key: string]: boolean}

  @computed get includeImageToExport(): boolean {
    const {exportOptionsView} = this
    return exportOptionsView && Object.keys(exportOptionsView).filter(f => exportOptionsView[f]).some(f => 'includeImage') || false
  }

  @computed get exportModalTitle() {
    if (!this?.catalog?.workspaceConfig?.catalog) return undefined
    const {catalog: {exportConfig}} = this
    return this.exportType ? exportConfig[this.exportType]?.modal?.modalTitle : 'Export'
  }

  @computed get defaultExportOptions() {
    const opts: Array<IExportOption> = this.exportOptions
    return opts?.reduce<{[key: string]: boolean}>((acc, v) => ({...acc, [v.value]: v?.default ?? false}), {}) ?? {}
  }

  @action setExportOptions(exportView) {
    if (!_.isEqual(exportView, this.exportOptionsView)) {
      this.exportOptionsView = exportView
    }
  }

  @action toggleExportOption = (e, value) => {
    const exportView = _.cloneDeep(this.exportOptionsView)
    exportView[value] = !exportView[value]
    this.setExportOptions(exportView)
  }

  @computed get disableDownload() {
    const {exportVLPListValue: {merchOptions, merchProducts, tags}} = this
    return merchOptions === false && merchProducts === false && tags === false
  }

  @action resetExportListValue = (): void => {
    const {catalog: {vlp}} = this,
      exportList = vlp.isVLPView ? this.exportVLPListValue : this.exportListValue
    Object.keys(exportList).forEach(k => exportList[k] = true)
    if (vlp.isVLPView) {
      this.exportVLPListValue = {...this.exportVLPListValue, ...exportList}
    } else {
      this.exportListValue = {...this.exportListValue, ...exportList}
    }
  }
  @action resetExportModal = (): void => {
    this.modalOpen = this.exportProgress = false
    this.setExportOptions(this.defaultExportOptions)
    this.selectedImageSize = 'Thumbnail'
  }

  @observable loadingImageSize: boolean = false

  @action onClose = () => {
    this.resetExportModal()
  }

  exportConfig: IExportConfig = {
    excel: {type: 'excel', modalTitle: 'Export to Excel'},
    images: {type: 'images', modalTitle: 'Export to Images'}
  }

  @observable exportImagesCount: number = 0
  @observable exportInitialConfig: IExportImagesConfig = {
    smallImageSize: 0,
    mediumImageSize: 0,
    largeImageSize: 0,
    originalImageSize: 0,
    thumbnailImageSize: 0,
    imageSize: 0
  }

  @observable exportImagesConfig: IExportImagesConfig = {...this.exportInitialConfig}

  onClickListItem = (e, val) => {
    if (this.catalog.vlp.isVLPView) {
      this.exportVLPListValue[val] = !this.exportVLPListValue[val]
    } else {
      this.exportListValue[val] = !this.exportListValue[val]
    }
  }

  @observable isExportInProgress = false

  @action onClickExport = (currentExport) => {
    if (currentExport.exportType) {
      this.exportType = currentExport.exportType
      this.modalOpen = true
    } else {
      onSiteError('Export type is missing in this export')
    }
  }

  checkImageSizeUnit = (size: number, imageType: string): string => {
    const {loadingImageSize} = this
    const sizeInInteger = parseInt(size.toString())
    return loadingImageSize ? `${imageType} ` : `${imageType} (${sizeInInteger > 1024 ? `${(sizeInInteger / 1024).toFixed(0)} MB` : `${sizeInInteger} KB`})`
  }

  @action excelExport = async () => {
    const {
        catalog: {
          selectedImageSourceFields, exportableFieldsByIndex, queryVariablesEs, catalogConfig, exportConfig, index, catalogItemName, imageSource, esProductSortBy,
        stores: {workspaces: {current}, site: {onError}}},
      } = this,
      fieldsToExport: Array<IExportableField> = exportableFieldsByIndex(index),
      additionalParams: IExportHelperParams = _.cloneDeep(exportConfig?.excel?.helperParams) ?? {}

    if (_.size(fieldsToExport) || _.size(additionalParams?.staticExportFields)) {
      if (this.includeImageToExport) {
        additionalParams.image.include = true
        additionalParams.image.fields = selectedImageSourceFields ?? []
        additionalParams.selectedImageConfig = this.selectedImageOption
      }
      //TODO: For now default image export is supported only for current workspace in the first row of excel, need to implement for nested workspaces
      const exportInput: IExportInput = {id: current, index: index, name: catalogItemName, fields: _.size(additionalParams?.staticExportFields) ? additionalParams?.staticExportFields : _.concat(fieldsToExport, additionalParams?.defaultExportFields ?? []), query: {query: queryVariablesEs?.query, sort: esProductSortBy?.sort}, nestedWorkspaces: []}
      exportInput.helperParams = additionalParams
      this.exportProgress = true
      setTimeout(() => {this.onClose()}, 2000)
      try {
        await downloadExcel(exportConfig?.excel?.filePrefix ?? 'vlp', this.exportURL, 'POST', {workbookInputs: [exportInput]})
      } catch (e) {
        onError(`Error while export excel ${e?.message}`)
      } finally {
        this.resetExportModal()
      }
      this.exportProgress = false
    } else {
      onError('Please select at least single view option')
    }
  }

  onDoExport = async () => {
    const {
      exportProgress,
      selectedImageSize,
      isImageExport,
      exportURL,
      exportType,
      exportOptions,
      catalog: {selectedImageSourceFields, queryVariablesEs, catalogConfig, exportConfig, index, catalogItemName, esProductSortBy, imageSource, workspaces: {current}},
    } = this

     let additionalParams: IExportHelperParams = _.cloneDeep(exportConfig[exportType]?.helperParams) ?? {}
    if (isImageExport && !exportProgress) {
      if(_.size(selectedImageSourceFields)) {
          additionalParams = {...additionalParams,image:{fields: selectedImageSourceFields}}
        const exportInput: IExportInput = {id: current, index: index, name: catalogItemName, fields: _.concat([], additionalParams?.defaultExportFields ?? []), query: {query: queryVariablesEs?.query, sort: esProductSortBy?.sort}, nameFields: additionalParams?.nameFields}
        exportInput.helperParams = additionalParams
        const name = `${exportConfig.images.filePrefix ?? 'images'}${moment(new Date()).format('MMDDYYYYHHmm')}`
        try {
          this.exportProgress = true
          await exportAndDownloadService(exportURL, 'POST', {workbookInputs: {...exportInput, selectedImageConfig: this.selectedImageOption}}, name)
        } catch (e) {
          onSiteError(`Error while export images ${e?.message}`)
        } finally {
          this.resetExportModal()
        }
      } else {
        onSiteError(`Please choose valid image source to export image`)
      }
    } else {
      await this.excelExport()
    }
  }

  onChangeImageSize = (e) => {
    this.selectedImageSize = e.currentTarget.value
  }
}



// WEBPACK FOOTER //
// ./src/stores/export/ExportStore.ts